import { v4 as uuidv4 } from "uuid";
import { shouldSkipClientAjaxForBot } from "@packages/lib/utlils/bot-detection";

const cache = new Map<string, any>();
const cachedPromise = new Map<string, Promise<any>>();

interface FetchWithCacheProps {
  url: string;
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  headers?: Record<string, string> | null;
  queryParams?: string | null;
  bodyjson?: any;
  isDomServiceBff?: boolean;
}

const optimizedSearch = async (
  url: string,
  method: string,
  headers: Record<string, string> | null,
  queryParams: string | null,
  bodyjson: any,
  isDomServiceBff: boolean = false
): Promise<any> => {
  const apiUrl = `${url}${queryParams ? `?${queryParams}&sitecode=${process.env.SITE_CODE}` : ""}`;

  if (shouldSkipClientAjaxForBot(apiUrl)) {
    return {};
  }

  if (cache.has(apiUrl)) {
    return cache.get(apiUrl);
  }

  try {

    const response = await fetch(apiUrl, {
      method: method.toUpperCase(),
      headers: {
        "Content-Type": "application/json",
        "x-correlation-id": uuidv4(),
        "x-api-key": `${isDomServiceBff ? process.env.NEXT_PUBLIC_DOMSERVICE_X_API_KEY : process.env.NEXT_PUBLIC_X_API_KEY}`,
        ...headers,
      },
      body: method === "GET" ? undefined : JSON.stringify(bodyjson),
    });

    if (!response.ok) {
      const errorResponse = await response.json().catch(() => ({}));
      console.error({
        error: errorResponse,
        endpoint: apiUrl,
        status: response?.status,
        statusText: response?.statusText,
      });
      throw new Error(`API Error: ${response?.status} ${response?.statusText}`);
    }

    const fetchedData = await response.json();
    cache.set(apiUrl, fetchedData);
    return fetchedData;
  } catch (error: unknown) {
    if (error instanceof Error) {
      console.error(`API call failed: ${error.message}`, { endpoint: apiUrl });
      throw error;
    } else {
      console.error("Unknown error occurred", { endpoint: apiUrl, error });
      throw new Error("An unknown error occurred during the API call.");
    }
  }
};

export const fetchWithCache = async ({
  url,
  method,
  headers,
  queryParams,
  bodyjson,
  isDomServiceBff = false,
}: FetchWithCacheProps): Promise<any> => {
  const apiUrl = `${url}${queryParams ? `?${queryParams}&sitecode=${process.env.SITE_CODE}` : ""}`;

  if (shouldSkipClientAjaxForBot(apiUrl)) {
    return {};
  }

  if (!cachedPromise.has(apiUrl)) {
    const fetchingPromise = fetch(apiUrl, {
      method: method.toUpperCase(),
      headers: {
        "Content-Type": "application/json",
        "x-correlation-id": uuidv4(),
        "x-api-key": `${isDomServiceBff ? process.env.NEXT_PUBLIC_DOMSERVICE_X_API_KEY : process.env.NEXT_PUBLIC_X_API_KEY}`,
        ...headers,
      },
      body: method === "GET" ? undefined : JSON.stringify(bodyjson),
    }).then((res) => res.json());

    cachedPromise.set(apiUrl, fetchingPromise);
  }
  return cachedPromise.get(apiUrl);
};

export default optimizedSearch;
